import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import { createPrivilegeObject } from "utils/privileges/privilegesMapper"
import workFlowDetailTransformer from "./workFlowDetailTranformer"
import workflowInitiateFormTranformer from "./workflowInitiateFormTranformer"
import requestWorkflowDetailTransformer from "./requestWorkflowDetailTransformer"
import TodoWorkflowTransformer from "./todoWorkflowTransformer"
import { definitionObject, initiationObject, stagesObject } from "./workflowListingTransformers"
import transformResponse from "features/shared/api/transformResponse"
import { skippableStagesTransformer } from "./skippableStagesTransformer"
import { todoColumnTransformer } from "./todoColumnTransformer"
import dayjs from "dayjs"
import { HH_MM_SS_FORMAT, YYYY_MM_DD_FORMAT } from "constants"
import { dateTimeFunction } from "utils/date"
import { workflowTaskTypesTransformer } from "./workflowTaskTypesTransformer"

const workFlowApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getCreateWorkFlowListing: build.query({
      query: params => `/workflows/template/search?${convertQueryObjToString(params)}`,
      transformResponse: response => {
        response?.workflowTemplateListing.forEach(e => {
          e["privileges"] = createPrivilegeObject(e?.privileges || {})
          return e
        })
        if (response) {
          response.transformedStatistics = response?.workflowTemplateStatistics?.reduce((value, currentValue) => {
            value[currentValue["status"]] = currentValue["statistics"]
            return value
          }, {})
        }

        return response
      },
      providesTags: ["workflow-listing"],
    }),
    getWorkflowTodoListing: build.query({
      query: p => {
        const params = { ...p }
        const checkDate = key => {
          if (!!params[key]) {
            const dateR = params[key].split(",")
            params[key] = encodeURIComponent(
              `{"from":"${dateTimeFunction({ date: dateR[0], timeZone: true, apiRequest: true })}","to":"${dateTimeFunction(
                {
                  date: dateR[1],
                  timeZone: true,
                  apiRequest: true,
                }
              )}"}`
            )
          }
        }
        checkDate("updatedDateRange")
        return `workflows/todos/search?${convertQueryObjToString(params)}`
      },
      transformResponse: data => TodoWorkflowTransformer(data),
      providesTags: ["workflowTodo-Listing"],
    }),
    getInitiateRequestListing: build.query({
      query: params => `/workflows/requests/search?${convertQueryObjToString(params)}`,
      transformResponse: response => {
        response?.workflowRequestListing?.listing.forEach(e => {
          e["privileges"] = createPrivilegeObject(e?.privileges || {})
          return e
        })

        let { listing, statistics } = response?.workflowRequestListing

        let transformedStatistics = statistics?.reduce((value, currentValue) => {
          value[currentValue["status"]] = currentValue["statistics"]
          return value
        }, {})

        return { listing, transformedStatistics }
      },
      providesTags: ["initiateRequest-listing"],
    }),

    getWorkFlowTemplates: build.query({
      query: params => `/workflows/templates?${convertQueryObjToString(params)}`,
      transformResponse: data => data?.dropDownData?.map(item => ({ label: item?.name, value: item?.id?.toString() })),
      providesTags: ["workflow-template"],
    }),
    getWorkFlowTodoTemplates: build.query({
      query: params => `workflows/todos/templates?${convertQueryObjToString(params)}`,
      transformResponse: data => data?.dropDownData?.map(item => ({ label: item?.name, value: item?.id?.toString() })),
    }),
    getWorkFlowRequestInitiators: build.query({
      query: () => `workflows/requests/requesters`,
      transformResponse: data => data?.users?.map(item => ({ label: item?.name, value: item?.id?.toString() })),
    }),

    getWorkFlowDetail: build.query({
      query: obj => `workflows/template/${obj?.id}?${convertQueryObjToString(obj?.params)}`,
      transformResponse: (response, meta, arg) =>
        arg?.params ? workFlowDetailTransformer(response?.data) : workflowInitiateFormTranformer(response?.data),
      providesTags: ["workflowDetail"],
    }),
    getWorkflowReportees: build.query({
      query: params => `/teamMembers/privilegedUsers?${convertQueryObjToString(params)}`,
      transformResponse: data => transformResponse.allStaffMembers(data),
    }),
    getRequestWorkflowDetail: build.query({
      query: obj => `workflows/request/${obj?.id}/detail?${convertQueryObjToString(obj?.params)}`,
      transformResponse: data => requestWorkflowDetailTransformer(data),
    }),
    createUpdateWorkFlow: build.mutation({
      query: ({ id, formObject }) => {
        let body
        if (!id) {
          body = definitionObject(formObject)
        }
        if (formObject?.requiredFields) {
          body = initiationObject(formObject, id)
        }
        if (formObject?.initiationForm) {
          body = stagesObject(formObject?.initiationForm, id)
        }

        return { url: "workflows/template", body: body, method: "PUT" }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["workflowDetail"] : []
      },
    }),

    initiateRequest: build.mutation({
      query: ({ templateId, values }) => {
        let body = {
          responseData: {
            ...values,
          },
        }
        return { url: `/workflows/${templateId}/request`, body: body, method: "POST" }
      },
    }),

    updateStatusOfWorkFlow: build.mutation({
      query: ({ templateId, status }) => {
        let body = {
          status: status,
        }
        return { url: `workflows/template/${templateId}/status`, body: body, method: "PUT" }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["workflow-listing", "workflow-template"] : []
      },
    }),

    modifyRequestStage: build.mutation({
      query: ({ templateId, stageId, data, skippedStageData, skippingReason }) => {
        let body = {
          ...(data?.action === "approved" && {
            stageData: data?.body,
            skippedStageData,
            skippingReason,
          }),
          modificationAction: data?.action,
          ...(data?.action === "rejected" && { rejectionReason: data?.body }),
          ...(data?.action === "re_evaluated" && {
            redirectionStageId: data?.body?.redirectionStageId,
            reEvaluationReason: data?.body?.comment,
          }),
          ...(data?.action === "skipped" && {
            skippedStageData,
            skippingReason,
          }),
        }
        return { url: `workflows/request/${templateId}/stage/${stageId}`, body: body, method: "PUT" }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["workflowTodo-Listing"] : []
      },
    }),
    changeWorkFlowAssignee: build.mutation({
      query: ({ templateId, stageId, userId }) => {
        return {
          url: `/workflows/request/${templateId}/stage/${stageId}/assignee`,
          body: { userId: userId },
          method: "PUT",
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["workflowTodo-Listing"] : []
      },
    }),

    getS3AttachmentUrl: build.query({
      query: queryParams => `workflows/getReadPreSignedURL?${convertQueryObjToString(queryParams)}`,
      transformResponse: data => data,
    }),
    getUserTeamDesignation: build.query({
      query: userId => `userDesignations/userTeamDesignations?userId=${userId}`,
      transformResponse: resp =>
        resp?.map(item => ({ label: localisedValue({ object: item, key: "name" }), value: item?.designationKey })),
    }),

    validateWorkflowLeadId: build.query({
      query: params => `workflows/templates/${params.templateId}/leads/${params.leadId}/validate`,
      transformResponse: response => response,
    }),
    getSkippedStages: build.query({
      query: ({ requestId, stageId }) => `/workflows/request/${requestId}/stage/${stageId}`,
      transformResponse: data => skippableStagesTransformer(data),
    }),
    getWorkflowInsights: build.query({
      query: params => `workflows/insights/${params.templateIds}`,
      transformResponse: response => response?.data,
    }),
    getWorkflowTaskTypes: build.query({
      query: params => `workflowStageTasks/taskTypes?${convertQueryObjToString(params)}`,
      transformResponse: (data, _, args) =>
        args?.hierarchy ? workflowTaskTypesTransformer(data?.taskTypes) : data?.taskTypes,
    }),
    getWorkflowTaskLogs: build.query({
      query: params => `workflowStageTasks/tasklogs?${convertQueryObjToString(params)}`,
      transformResponse: data => data?.tasksLogs,
    }),
    getWorkflowTodosColumns: build.query({
      query: () => `workflows/todoColumns?entityType=lead`,
      transformResponse: data => todoColumnTransformer(data?.data),
      providesTags: ["workflowTodo-columns"],
    }),
    addTaskWorkflowStages: build.mutation({
      query: ({ values, selectedRequestItem }) => {
        let completionDate = dayjs(
          values?.completionDate?.format(YYYY_MM_DD_FORMAT) +
            " " +
            (values?.completionTime?.[0] ? values?.completionTime?.[0].format(HH_MM_SS_FORMAT) : "")
        )
        let todoDeadline = dayjs(
          values?.deadLine?.format(YYYY_MM_DD_FORMAT) +
            " " +
            (values?.deadLineTime?.[0] ? values?.deadLineTime?.[0].format(HH_MM_SS_FORMAT) : "")
        )
        let payload = {
          stageTemplateId: selectedRequestItem?.stageId,
          requestId: selectedRequestItem?.requestId,
          taskTypeId: values?.subTask,
          nextTaskTypeId: values?.nextTask,
          comments: values?.comment,
          todoDeadline: todoDeadline,
          completionDate: completionDate,
          ...(values?.attachments?.length > 0 && {
            attachments: values?.attachments?.map(e => {
              return { file: e?.file?.split(",")[1], name: e?.name }
            }),
          }),
          ...(!!values.voiceComment && {
            commentVoiceRecording: {
              data: values.voiceComment.split(",")[1],
              name: "voice-recording.webm",
              type: "audio/webm",
            },
          }),
        }
        return { url: `/workflowStageTasks/task`, body: payload, method: "POST" }
      },
    }),
    updateUserTodosColumn: build.mutation({
      query: params => {
        return {
          url: `workflows/todoColumns${!params?.length ? "?entityType=lead" : ""}`,
          method: params?.length ? "POST" : "DELETE",
          ...(params?.length && {
            body: {
              entityType: "lead",
              defaultColumns: params?.map((column, index) => ({
                workflowTodoDisplayColumnId: column,
                columnSequence: index + 1,
              })),
            },
          }),
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["workflowTodo-Listing", "workflowTodo-columns"] : []
      },
    }),
  }),
})

export const {
  useGetCreateWorkFlowListingQuery,
  useGetWorkFlowTemplatesQuery,
  useCreateUpdateWorkFlowMutation,
  useGetWorkFlowDetailQuery,
  useLazyGetWorkFlowDetailQuery,
  useGetWorkflowReporteesQuery,
  useLazyGetWorkflowReporteesQuery,
  useUpdateStatusOfWorkFlowMutation,
  useGetInitiateRequestListingQuery,
  useGetWorkFlowRequestInitiatorsQuery,
  useInitiateRequestMutation,
  useLazyGetWorkFlowTemplatesQuery,
  useLazyGetRequestWorkflowDetailQuery,
  useGetWorkflowTodoListingQuery,
  useLazyGetWorkflowTodoListingQuery,
  useGetWorkflowTodosColumnsQuery,
  useLazyGetWorkflowTodosColumnsQuery,
  useGetWorkflowTaskTypesQuery,
  useLazyGetWorkflowTaskTypesQuery,
  useGetWorkflowTaskLogsQuery,
  useLazyGetWorkflowTaskLogsQuery,
  useUpdateUserTodosColumnMutation,
  useGetWorkFlowTodoTemplatesQuery,
  useLazyGetWorkFlowTodoTemplatesQuery,
  useModifyRequestStageMutation,
  useGetS3AttachmentUrlQuery,
  useLazyGetS3AttachmentUrlQuery,
  useChangeWorkFlowAssigneeMutation,
  useLazyGetUserTeamDesignationQuery,
  useValidateWorkflowLeadIdQuery,
  useLazyValidateWorkflowLeadIdQuery,
  useLazyGetSkippedStagesQuery,
  useGetWorkflowInsightsQuery,
  useLazyGetWorkflowInsightsQuery,
  useAddTaskWorkflowStagesMutation,
} = workFlowApi
